import { api } from "./http";
import { asJson, asMultipart } from "./headers";

function appendParam(params, key, value) {
  if (value === undefined || value === null || value === "") return;
  params.append(key, value);
}

function buildNewsParams(filters = {}) {
  const params = new URLSearchParams();

  appendParam(params, "languageCode", filters.languageCode);
  appendParam(params, "search", filters.search);
  appendParam(params, "archived", filters.archived);
  appendParam(params, "starred", filters.starred);
  appendParam(params, "publishedFrom", filters.publishedFrom);
  appendParam(params, "publishedTo", filters.publishedTo);
  appendParam(params, "page", filters.page);
  appendParam(params, "pageSize", filters.pageSize);

  (filters.facetIds || []).forEach((facetId) => {
    appendParam(params, "facetIds", facetId);
  });

  return params;
}

function buildUploadFormData(file) {
  const formData = new FormData();
  formData.append("file", file, file?.name || "news-image.png");
  return formData;
}

export const getNewsArticles = async (filters = {}) => {
  return api.get("/news/articles", asJson({ params: buildNewsParams(filters) }));
};

export const getNewsArticle = async (articleId, filters = {}) => {
  return api.get(
    `/news/articles/${articleId}`,
    asJson({ params: buildNewsParams(filters) }),
  );
};

export const getNewsArticleBySlug = async (slug, filters = {}) => {
  return api.get(
    `/news/articles/by-slug/${encodeURIComponent(slug)}`,
    asJson({ params: buildNewsParams(filters) }),
  );
};

export const getNewsFacets = async (filters = {}) => {
  return api.get("/news/facets", asJson({ params: buildNewsParams(filters) }));
};

export const starNewsArticle = async (articleId) => {
  return api.put(`/news/articles/${articleId}/star`, undefined, asJson());
};

export const unstarNewsArticle = async (articleId) => {
  return api.delete(`/news/articles/${articleId}/star`, asJson());
};

export const getAdminNewsArticles = async (filters = {}) => {
  return api.get(
    "/admin/news/articles",
    asJson({ params: buildNewsParams(filters) }),
  );
};

export const getAdminNewsArticle = async (articleId, filters = {}) => {
  return api.get(
    `/admin/news/articles/${articleId}`,
    asJson({ params: buildNewsParams(filters) }),
  );
};

export const getAdminNewsArticleBySlug = async (slug, filters = {}) => {
  return api.get(
    `/admin/news/articles/by-slug/${encodeURIComponent(slug)}`,
    asJson({ params: buildNewsParams(filters) }),
  );
};

export const createNewsArticle = async (payload) => {
  return api.post("/admin/news/articles", payload, asJson());
};

export const updateNewsArticleTranslation = async (
  articleId,
  languageCode,
  payload,
) => {
  return api.put(
    `/admin/news/articles/${articleId}/translations/${languageCode}`,
    payload,
    asJson(),
  );
};

export const aiTranslateNewsArticle = async (articleId, languageCode) => {
  return api.post(
    `/admin/news/articles/${articleId}/translations/${languageCode}/ai-translate`,
    undefined,
    asJson(),
  );
};

export const aiTranslateNewsArticleDraft = async (payload) => {
  return api.post("/admin/news/articles/draft-ai-translate", payload, asJson());
};

export const archiveNewsArticle = async (articleId) => {
  return api.post(`/admin/news/articles/${articleId}/archive`, undefined, asJson());
};

export const restoreNewsArticle = async (articleId) => {
  return api.post(`/admin/news/articles/${articleId}/restore`, undefined, asJson());
};

export const deleteNewsArticle = async (articleId) => {
  return api.delete(`/admin/news/articles/${articleId}`, asJson());
};

export const uploadNewsArticleCoverImage = async (articleId, file) => {
  return api.post(
    `/admin/news/articles/${articleId}/cover-image`,
    buildUploadFormData(file),
    asMultipart(),
  );
};

export const deleteNewsArticleCoverImage = async (articleId) => {
  return api.delete(`/admin/news/articles/${articleId}/cover-image`, asJson());
};

export const getAdminNewsFacets = async (filters = {}) => {
  return api.get("/admin/news/facets", asJson({ params: buildNewsParams(filters) }));
};

export const createNewsFacet = async (payload) => {
  return api.post("/admin/news/facets", payload, asJson());
};

export const reorderNewsFacets = async (orderedIds) => {
  return api.post("/admin/news/facets/reorder", { orderedIds }, asJson());
};

export const updateNewsFacetTranslation = async (
  facetId,
  languageCode,
  payload,
) => {
  return api.put(
    `/admin/news/facets/${facetId}/translations/${languageCode}`,
    payload,
    asJson(),
  );
};

export const deleteNewsFacet = async (facetId) => {
  return api.delete(`/admin/news/facets/${facetId}`, asJson());
};

export const uploadNewsFacetIcon = async (facetId, file) => {
  return api.post(
    `/admin/news/facets/${facetId}/icon`,
    buildUploadFormData(file),
    asMultipart(),
  );
};

export const deleteNewsFacetIcon = async (facetId) => {
  return api.delete(`/admin/news/facets/${facetId}/icon`, asJson());
};
